import { isIOS, isMacOs } from 'react-device-detect'

export const CMD = 'platform-specific-command'
export const MOD = 'mod' // "mod" is PM's version of "platform-specific-command"
// The values of these are recognized by the useHotkeys mantine hook
export const DEL = 'Backspace'
export const ALT = 'alt'
export const SHIFT = 'shift'
export const TAB = 'Tab'
export const ENTER = 'Enter'
export const CTRL = 'Control'
export const RIGHT = '→'
export const LEFT = '←'
export const UP = '↑'
export const DOWN = '↓'

const MODIFIER_KEYS = [CTRL, CMD, ALT, SHIFT] as const
export type ModifierKey = (typeof MODIFIER_KEYS)[number]
export type Platform = 'mac' | 'win'

export const detectedPlatform: Platform = isIOS || isMacOs ? 'mac' : 'win'

function isModifierKey(value: string): value is ModifierKey {
  return MODIFIER_KEYS.includes(value as ModifierKey)
}

const winSortOrder: Record<ModifierKey, number> = {
  [CTRL]: 0,
  [CMD]: 1,
  [SHIFT]: 2,
  [ALT]: 3,
}

const macSortOrder: Record<ModifierKey, number> = {
  [CTRL]: 0,
  [CMD]: 3,
  [SHIFT]: 1,
  [ALT]: 2,
}

const keyRepresentation = (key: string, platform: Platform): string => {
  const isWin = platform === 'win'
  switch (key) {
    case MOD:
    case CMD:
      return isWin ? 'Ctrl' : '⌘'
    case SHIFT:
      return isWin ? 'Shift' : '⇧'
    case ALT:
      return isWin ? 'Alt' : '⌥'
    case CTRL:
      return isWin ? 'Ctrl' : '⌃'
    case ENTER:
      return isWin ? 'Enter' : 'Return'
    case DEL:
      return isWin ? 'Backspace' : 'Delete'
    default:
      return key
  }
}

export const sortKeys = (keys: string[], platform: Platform): string[] => {
  const sortOrder = platform === 'mac' ? macSortOrder : winSortOrder
  return [...keys].sort((k1, k2) => {
    const k1sort = isModifierKey(k1) ? sortOrder[k1] : keys.indexOf(k1) + 10
    const k2sort = isModifierKey(k2) ? sortOrder[k2] : keys.indexOf(k2) + 10
    return k1sort - k2sort
  })
}

/**
 * Display a key combination in the style of the host platform
 */
export const buildShortcutTip = (
  keys: string[],
  platform: Platform = detectedPlatform,
): string => {
  const separator = platform === 'win' ? '+' : ''

  return sortKeys(keys, platform)
    .map((key) => keyRepresentation(key, platform))
    .join(separator)
}

/**
 * Prosemirror uses a different key combo syntax for setting up the editor's
 * keymap. This builds the array of keys from one of those combo strings
 */
export function keysFromProsemirror(keyCombo: string) {
  return keyCombo.split('-').map((part) => {
    switch (part) {
      case 'Mod':
        return CMD
      case 'Shift':
        return SHIFT
      case 'Ctrl':
        return CTRL
      case 'Alt':
        return ALT
      default:
        return part.toUpperCase()
    }
  })
}

const platformCmd = (key: string, platform: Platform): string => {
  if (key === CMD) {
    return platform === 'win' ? 'ctrl' : 'meta'
  }
  return key
}

export const buildShortcutCode = (keys: string[], platform: Platform) =>
  keys.map((k) => platformCmd(k, platform)).join('+')
